'use client';

import { useCallback, useContext, useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  Flex,
  Icon,
  NativeSelect,
  Portal,
  Spinner,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { MdCheckCircle, MdOutlineDescription } from 'react-icons/md';
import {
  GoogleReCaptcha,
  GoogleReCaptchaProvider,
} from 'react-google-recaptcha-v3';
import { AuthContext } from '@/contexts/AuthContext';
import { useQuoteRequest } from '@/contexts/QuoteRequestContext';
import { api } from '@/services/api';
import { StoreProps } from '@/types/Store';
import { ResponseError } from '@/types/Error';
import FormInput from '@/components/Form/form-input';
import ErrorMessage from '@/components/ErrorMessage';
import { pushDataLayerEvent } from '@/utils/dataLayer';
import { trackMetaEvent } from '@/utils/metaPixel';

const LENS_TYPES: { value: string; label: string }[] = [
  { value: 'FAR', label: 'Lentes para Longe' },
  { value: 'NEAR', label: 'Lentes para Perto' },
  { value: 'PROGRESSIVE', label: 'Lentes Progressivas (Multifocais)' },
  { value: 'CONTACT_LENSES', label: 'Lentes de Contacto' },
];

const MAX_ATTACHMENT_SIZE = 2 * 1024 * 1024; // 2MB
const ALLOWED_ATTACHMENT_TYPES = ['application/pdf', 'image/png', 'image/jpeg'];

const schema = z.object({
  first_name: z.string().min(1, { message: 'Nome é obrigatório' }),
  last_name: z.string().min(1, { message: 'Apelido é obrigatório' }),
  email: z.string().email({ message: 'Email inválido' }),
  phone: z.string().min(1, { message: 'Telemóvel é obrigatório' }),
  lens_type: z.string().min(1, { message: 'Escolha o tipo de lente' }),
  store_id: z.string().min(1, { message: 'Escolha uma loja' }),
  message: z.string().optional(),
  // Validado manualmente (tamanho/tipo) no onSubmit — aqui só precisa de
  // não ser removido pelo zodResolver, que por omissão descarta chaves não
  // declaradas no schema antes de chamar onSubmit.
  attachment: z.any().optional(),
  recaptchaToken: z.string().min(1, { message: 'reCAPTCHA é obrigatório' }),
});

type FormData = z.infer<typeof schema> & { attachment?: FileList };

const QuoteRequestModal = () => {
  const { isOpen, closeModal } = useQuoteRequest();
  const { profile } = useContext(AuthContext);

  const [stores, setStores] = useState<StoreProps[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  useEffect(() => {
    if (!isOpen) return;

    api
      .get('/stores/list/active')
      .then((response) => setStores(response.data))
      .catch(() => setStores([]));

    if (profile) {
      const [firstName, ...rest] = profile.name.split(' ');
      setValue('first_name', firstName);
      if (rest.length > 0) setValue('last_name', rest.join(' '));
      setValue('email', profile.email);
    }
  }, [isOpen, profile, setValue]);

  const onSubmit = useCallback(
    async (data: FormData) => {
      setIsLoading(true);
      setErrorMessage('');

      const file = data.attachment?.[0];
      if (file) {
        if (file.size > MAX_ATTACHMENT_SIZE) {
          setErrorMessage('O ficheiro não pode exceder 2MB.');
          setIsLoading(false);
          return;
        }
        if (!ALLOWED_ATTACHMENT_TYPES.includes(file.type)) {
          setErrorMessage(
            'Formato de ficheiro não permitido. Use PDF, PNG, JPG ou JPEG.',
          );
          setIsLoading(false);
          return;
        }
      }

      try {
        const formData = new FormData();
        formData.append('first_name', data.first_name);
        formData.append('last_name', data.last_name);
        formData.append('email', data.email);
        formData.append('phone', data.phone);
        formData.append('lens_type', data.lens_type);
        formData.append('store_id', data.store_id);
        if (data.message) formData.append('message', data.message);
        if (profile?.id) formData.append('customer_id', profile.id);
        formData.append('recaptcha_token', data.recaptchaToken);
        if (file) formData.append('attachment', file);

        await api.post('/quote-requests', formData);

        pushDataLayerEvent('generate_lead', {
          form_name: 'pedido_orcamento',
        });
        trackMetaEvent('Lead', { content_name: 'pedido_orcamento' });

        setSubmitted(true);
      } catch (error) {
        const e = error as ResponseError;
        setErrorMessage(
          e.response && e.response.data
            ? e.response.data.message
            : 'Não foi possível enviar o pedido. Tente novamente.',
        );
      } finally {
        setIsLoading(false);
      }
    },
    [profile],
  );

  const handleClose = useCallback(
    (e: Dialog.OpenChangeDetails) => {
      if (isLoading) return;
      if (!e.open) {
        closeModal();
        reset();
        setErrorMessage('');
        setSubmitted(false);
      }
    },
    [isLoading, closeModal, reset],
  );

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={handleClose}
      closeOnInteractOutside={!isLoading}
      closeOnEscape={!isLoading}
      size="sm"
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content
            bg={{ base: '#FFFFFF', _dark: '#0A1B2B' }}
            borderRadius="20px"
            border="2px solid"
            borderColor={{ base: 'rgba(0,0,0,0.07)', _dark: 'rgba(255,255,255,0.06)' }}
            boxShadow="0 8px 40px rgba(0,0,0,0.12)"
          >
            <Dialog.CloseTrigger />
            <Dialog.Header>
              <Dialog.Title>Pedir Orçamento</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body pb="24px">
              {submitted ? (
                <Flex flexDir="column" align="center" gap="12px" py="20px">
                  <Icon fontSize="44px" color="#22C55E">
                    <MdCheckCircle />
                  </Icon>
                  <Text fontSize="16px" fontWeight="700" textAlign="center">
                    Pedido enviado!
                  </Text>
                  <Text
                    fontSize="14px"
                    textAlign="center"
                    color={{ base: 'rgba(0,0,0,0.6)', _dark: 'rgba(255,255,255,0.6)' }}
                  >
                    Um dos nossos especialistas vai analisar o seu pedido e
                    entrará em contacto consigo brevemente.
                  </Text>
                  <Button
                    mt="8px"
                    borderRadius="full"
                    bg="#00B0CA"
                    color="#fff"
                    fontWeight="700"
                    px="28px"
                    _hover={{ bg: '#009AB5' }}
                    onClick={() => handleClose({ open: false } as Dialog.OpenChangeDetails)}
                  >
                    Fechar
                  </Button>
                </Flex>
              ) : (
                // O provider só existe enquanto o modal está aberto — este
                // componente fica sempre montado no layout (para poder ser
                // aberto a partir de qualquer página), e o script/badge do
                // reCAPTCHA não deve aparecer no site inteiro o tempo todo.
                isOpen && (
                <GoogleReCaptchaProvider
                  reCaptchaKey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || ''}
                >
                <Flex
                  as="form"
                  flexDir="column"
                  gap="14px"
                  onSubmit={handleSubmit(onSubmit)}
                >
                  <Text
                    fontSize="13px"
                    color={{ base: 'rgba(0,0,0,0.6)', _dark: 'rgba(255,255,255,0.55)' }}
                  >
                    Preencha os seus dados. A nossa equipa entra em contacto
                    para lhe apresentar o orçamento.
                  </Text>

                  <Flex gap="10px">
                    <FormInput
                      placeholder="Nome"
                      width="50%"
                      error={errors.first_name?.message}
                      {...register('first_name')}
                    />
                    <FormInput
                      placeholder="Apelido"
                      width="50%"
                      error={errors.last_name?.message}
                      {...register('last_name')}
                    />
                  </Flex>
                  <FormInput
                    placeholder="Email"
                    width="100%"
                    type="email"
                    error={errors.email?.message}
                    {...register('email')}
                  />
                  <FormInput
                    placeholder="Telemóvel"
                    width="100%"
                    error={errors.phone?.message}
                    {...register('phone')}
                  />

                  <NativeSelect.Root size="md" w="full">
                    <NativeSelect.Field
                      placeholder="Tipo de lente"
                      {...register('lens_type')}
                    >
                      {LENS_TYPES.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                  {errors.lens_type?.message && (
                    <ErrorMessage message={errors.lens_type.message} />
                  )}

                  <NativeSelect.Root size="md" w="full">
                    <NativeSelect.Field
                      placeholder="Escolha a loja"
                      {...register('store_id')}
                    >
                      {stores.map((store) => (
                        <option key={store.id} value={store.id}>
                          {store.name}
                        </option>
                      ))}
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                  {errors.store_id?.message && (
                    <ErrorMessage message={errors.store_id.message} />
                  )}

                  <Textarea
                    placeholder="Mensagem"
                    rows={3}
                    borderRadius="16px"
                    border="1.5px solid"
                    borderColor={{ base: 'rgba(0,176,202,0.2)', _dark: 'rgba(0,176,202,0.15)' }}
                    _focus={{ borderColor: '#00B0CA', boxShadow: '0 0 0 3px rgba(0,176,202,0.12)', outline: 'none' }}
                    {...register('message')}
                  />
                  <Text
                    fontSize="12px"
                    mt="-8px"
                    color={{ base: 'rgba(0,0,0,0.45)', _dark: 'rgba(255,255,255,0.4)' }}
                  >
                    Nota: caso não tenha um documento com a sua graduação, por
                    favor indique-a no seu pedido. Não conseguimos responder ao
                    pedido de orçamento sem a graduação necessária.
                  </Text>

                  <Flex flexDir="column" gap="4px">
                    <Flex
                      align="center"
                      gap="8px"
                      px="12px"
                      py="10px"
                      borderRadius="14px"
                      border="1.5px dashed rgba(0,176,202,0.35)"
                      bg={{ base: 'rgba(0,176,202,0.04)', _dark: 'rgba(0,176,202,0.06)' }}
                    >
                      <Icon color="#00B0CA" fontSize="18px" flexShrink={0}>
                        <MdOutlineDescription />
                      </Icon>
                      <Flex flexDir="column" flex="1">
                        <Text fontSize="13px" fontWeight="600">
                          Carregue aqui a sua receita (opcional)
                        </Text>
                        <input
                          type="file"
                          accept=".pdf,.png,.jpg,.jpeg"
                          {...register('attachment')}
                          style={{ fontSize: '12px' }}
                        />
                      </Flex>
                    </Flex>
                    <Text
                      fontSize="11px"
                      color={{ base: 'rgba(0,0,0,0.45)', _dark: 'rgba(255,255,255,0.4)' }}
                    >
                      Até 2MB. Ficheiros permitidos: PDF, PNG, JPG, JPEG.
                    </Text>
                  </Flex>

                  <GoogleReCaptcha
                    onVerify={(token) => setValue('recaptchaToken', token)}
                  />
                  {errors.recaptchaToken?.message && (
                    <ErrorMessage message={errors.recaptchaToken.message} />
                  )}

                  {errorMessage && <ErrorMessage message={errorMessage} />}

                  <Button
                    type="submit"
                    w="full"
                    h="44px"
                    borderRadius="full"
                    bg="#00B0CA"
                    color="#FFFFFF"
                    fontWeight="700"
                    fontSize="14px"
                    boxShadow="0 4px 20px rgba(0,176,202,0.3)"
                    transition="all 0.2s"
                    disabled={isLoading}
                    _hover={{ bg: '#009AB5' }}
                    _active={{ bg: '#008299', transform: 'scale(0.98)' }}
                    _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
                  >
                    {isLoading ? <Spinner size="sm" color="white" /> : 'Pedir Orçamento'}
                  </Button>
                </Flex>
                </GoogleReCaptchaProvider>
                )
              )}
            </Dialog.Body>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default QuoteRequestModal;
